import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTransition } from "@/context/UIContext";
import { BRAND, NAV } from "@/data/content";

export default function MobileMenu({ open, onClose }) {
    const [picked, setPicked] = useState(null);
    const { go } = useTransition();

    const handle = (item, i) => {
        if (picked !== null) return;
        setPicked(i);
        setTimeout(() => {
            onClose();
            go(item.path, item.transition);
            setPicked(null);
        }, 430);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[55] lg:hidden"
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    data-testid="mobile-menu"
                >
                    <motion.div
                        className="absolute inset-0 bg-navy-900"
                        variants={{
                            hidden: { clipPath: "circle(0% at 92% 4%)" },
                            show: {
                                clipPath: "circle(150% at 92% 4%)",
                                transition: { duration: 0.65, ease: [0.76, 0, 0.24, 1] },
                            },
                            exit: { clipPath: "circle(0% at 92% 4%)", transition: { duration: 0.4, ease: [0.76, 0, 0.24, 1] } },
                        }}
                    />
                    <motion.div
                        aria-hidden
                        className="absolute inset-0"
                        variants={{
                            hidden: { opacity: 0 },
                            show: { opacity: 1, transition: { delay: 0.4, duration: 0.8 } },
                            exit: { opacity: 0 },
                        }}
                    >
                        <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-electric/20 blur-[110px] animate-pulse-glow" />
                        <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-electric/10 blur-[100px] animate-pulse-glow" style={{ animationDelay: "1.2s" }} />
                    </motion.div>

                    <motion.div
                        variants={{
                            hidden: { opacity: 0 },
                            show: { opacity: 1, transition: { delay: 0.25 } },
                            exit: { opacity: 0, transition: { duration: 0.25 } },
                        }}
                        className="relative flex h-full flex-col px-6 pb-8 pt-24"
                    >
                        <nav className="flex-1" data-testid="mobile-nav-list">
                            {NAV.map((item, i) => (
                                <motion.button
                                    key={item.path}
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                    onClick={() => handle(item, i)}
                                    className="flex w-full items-baseline gap-4 border-b border-white/5 py-3 text-left"
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: picked === null || picked === i ? 1 : 0.35, x: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ delay: 0.35 + i * 0.055, duration: 0.4 }}
                                >
                                    <span className="font-mono text-xs text-electric">0{i + 1}</span>
                                    <span className="overflow-hidden">
                                        <motion.span
                                            className={`block font-display text-[22px] ${picked === i ? "text-electric" : "text-ice"}`}
                                            initial={{ y: "110%" }}
                                            animate={{ y: 0 }}
                                            transition={{ delay: 0.4 + i * 0.055, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                        >
                                            {item.label}
                                        </motion.span>
                                    </span>
                                </motion.button>
                            ))}
                        </nav>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ delay: 0.85, duration: 0.5 }}
                        >
                            <p className="font-display text-lg leading-snug text-ice">Let's Build Something Powerful.</p>
                            <a href={`mailto:${BRAND.email}`} className="mt-3 block break-words font-mono text-sm text-electric" data-testid="mobile-menu-email">
                                {BRAND.email}
                            </a>
                            {BRAND.phones.map((phone) => <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="mt-2 block text-xs text-frost transition-colors hover:text-electric">{phone}</a>)}
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
